import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../store/authStore';
import  api  from '../../services/api';
import { Shield, AlertTriangle, Clock, CheckCircle } from 'lucide-react';
// import { UserRole } from '../types/';
export enum UserRole {
  BUYER = 'BUYER',
  VENDOR = 'VENDOR',
  ADMIN = 'ADMIN',
  MODERATOR = 'MODERATOR',
  AGENCY = 'AGENCY'
}
interface AgencyAccessGuardProps {
  children: React.ReactNode;
  requireApproval?: boolean;
}

interface AgencyStatus {
  isAgency: boolean;
  isApproved: boolean;
  agencyName?: string;
  agencyId?: string;
  approvalStatus: 'pending' | 'approved' | 'rejected' | 'not_agency';
}

const AgencyAccessGuard: React.FC<AgencyAccessGuardProps> = ({ 
  children, 
  requireApproval = true 
}) => {
  const { user, isAuthenticated } = useAuthStore();
  const navigate = useNavigate();
  const [agencyStatus, setAgencyStatus] = useState<AgencyStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isAuthenticated || !user) {
      navigate('/login');
      return;
    }

    checkAgencyStatus();
  }, [user, isAuthenticated, navigate]);

  const checkAgencyStatus = async () => {
    try {
      setLoading(true);
      setError(null);

      // Check if user is marked as agency in auth
      if (!user || user.role !== UserRole.AGENCY) {
        console.log('user======>>>>>>', user);
        setAgencyStatus({
          isAgency: false,
          isApproved: false,
          approvalStatus: 'not_agency'
        });
        setLoading(false);
        return;
      }

      // Always call the API to get the most up-to-date agency status
        try {
        const response = await api.get('/api/gov/agency-status') as any;
        console.log('Full API response======>>>>>>', response);
        console.log('Response success======>>>>>>', response.success);
        console.log('Response data======>>>>>>', response.data);
        
        if (response.success && response.data) {
          const agencyStatus = response.data;
          console.log('agencyStatus======>>>>>>', agencyStatus);
          setAgencyStatus({
            isAgency: agencyStatus.isAgency,
            isApproved: agencyStatus.isApproved,
            agencyName: agencyStatus.agencyName,
            agencyId: agencyStatus.agencyId,
            approvalStatus: agencyStatus.approvalStatus
          });
        } else {
          throw new Error('Agency status not found');
        }
      } catch (apiError) {
        console.error('Error checking agency status:', apiError);
        // If API call fails but user is marked as AGENCY, assume incomplete registration
        setAgencyStatus({
          isAgency: true,
          isApproved: false,
          approvalStatus: 'pending'
        });
      }
    } catch (err) {
      console.error('Error in agency access guard:', err);
      setError('Failed to verify agency status');
    } finally {
      setLoading(false);
    }
  };

  // Show loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Verifying agency access...</p>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md mx-auto text-center">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
            <AlertTriangle className="h-16 w-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Verification Error</h2>
            <p className="text-gray-600 mb-6">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors mr-4"
            >
              Try Again
            </button>
            <button
              onClick={() => navigate('/')}
              className="bg-gray-300 text-gray-700 px-6 py-2 rounded-lg hover:bg-gray-400 transition-colors"
            >
              Go Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Check access based on agency status
  if (!agencyStatus) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md mx-auto text-center">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
            <AlertTriangle className="h-16 w-16 text-yellow-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Unable to Verify Status</h2>
            <p className="text-gray-600 mb-6">
              We couldn't verify your agency status. Please contact support if this continues.
            </p>
            <button
              onClick={() => navigate('/')}
              className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Return to Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Not an agency member
  if (!agencyStatus.isAgency) {
    console.log('agencyStatus======>>>>>>', agencyStatus.isAgency);
    console.log('Full agencyStatus object======>>>>>>', agencyStatus);
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md mx-auto text-center">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
            <Shield className="h-16 w-16 text-blue-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Government Agency Access Required</h2>
            <p className="text-gray-600 mb-6">
              This page is only accessible to verified government agency members.
            </p>
            <div className="space-y-3">
              <button
                onClick={() => navigate('/gov/register')}
                className="w-full bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
              >
                Register as Government Agency
              </button>
              <button
                onClick={() => navigate('/')}
                className="w-full bg-gray-300 text-gray-700 px-6 py-2 rounded-lg hover:bg-gray-400 transition-colors"
              >
                Return to Home
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Agency member but not approved (and approval is required)
  if (requireApproval && !agencyStatus.isApproved) {
    console.log('agencyStatus======>', agencyStatus);
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md mx-auto text-center">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
            <Clock className="h-16 w-16 text-orange-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Account Pending Verification</h2>
            <p className="text-gray-600 mb-4">
              Your government agency account is pending admin verification.
            </p>
            
            {agencyStatus.agencyName && (
              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <p className="text-sm text-gray-600">
                  <strong>Agency:</strong> {agencyStatus.agencyName}
                </p>
                <p className="text-sm text-gray-600">
                  <strong>Status:</strong> Pending Review
                </p>
              </div>
            )}
            
            <div className="text-left bg-blue-50 rounded-lg p-4 mb-6">
              <h3 className="font-semibold text-blue-900 mb-2">What happens next?</h3>
              <ul className="text-sm text-blue-800 space-y-1">
                <li>• Our admin team will review your application</li>
                <li>• Verification typically takes 1-3 business days</li>
                <li>• You'll receive an email notification once approved</li>
                <li>• After approval, you'll have full access to government features</li>
              </ul>
            </div>
            
            <div className="space-y-3">
              <button
                onClick={() => checkAgencyStatus()}
                className="w-full bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
              >
                Check Status Again
              </button>
              <button
                onClick={() => navigate('/')}
                className="w-full bg-gray-300 text-gray-700 px-6 py-2 rounded-lg hover:bg-gray-400 transition-colors"
              >
                Return to Home
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // All checks passed - render children
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Success indicator for approved agencies */}
      {agencyStatus.isApproved && (
        <div className="bg-green-50 border-b border-green-200 px-4 py-2">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <CheckCircle className="h-5 w-5 text-green-600" />
              <span className="text-sm font-medium text-green-800">
                Verified Government Agency: {agencyStatus.agencyName}
              </span>
            </div>
          </div>
        </div>
      )}
      
      {children}
    </div>
  );
};

export default AgencyAccessGuard; 